import type { FormConfig } from "./types";

const cache = new Map<string, Promise<FormConfig>>();

/**
 * Fetches /forms/{formId}.json relative to the embed script's own origin. With a
 * `previewRev` (the admin's preview links carry one) it reads that publish's own
 * copy under /previews/ instead, which no cache has an older version of.
 *
 * A preview copy can be gone: the bucket expires them after a week, and staging's
 * database is restored from production nightly, so a form's latest revision there
 * may name a copy only production's bucket has. A page preview then falls back to
 * the live config rather than showing a hole where the form should be.
 */
export function loadFormConfig(originUrl: string, formId: string, previewRev?: string): Promise<FormConfig> {
  if (!previewRev) return fetchConfig(originUrl, formId, `forms/${formId}.json`);
  return fetchConfig(originUrl, formId, `previews/${formId}-${previewRev}.json`).catch((err) => {
    console.warn(`hrn-forms: preview copy ${previewRev} of "${formId}" is not available, showing the live config`, err);
    return fetchConfig(originUrl, formId, `forms/${formId}.json`);
  });
}

function fetchConfig(originUrl: string, formId: string, path: string): Promise<FormConfig> {
  const cacheKey = originUrl + path;
  let pending = cache.get(cacheKey);
  if (!pending) {
    pending = fetch(`${originUrl}/${path}`, { credentials: "omit" }).then((res) => {
      if (!res.ok) throw new Error(`hrn-forms: config not found for "${formId}" (${res.status})`);
      return res.json() as Promise<FormConfig>;
    });
    cache.set(cacheKey, pending);
  }
  return pending;
}
